import React, { useState, useEffect } from 'react';
import { Form, Button, Select, Icon } from 'semantic-ui-react';
import { getInvestorProfileBadges } from '~/api/company';
import CompanyBadge from '~/features/company/CompanyBadge';

import { STATES } from '~/utils';
import './styles.scss';

interface FilterProps {
    onFilterChange: (filters: any) => void;
    triggerReset: boolean;
}

const maCapacityOptions = [
    { key: '$500M+', text: '$500M+', value: '$500M+' },
    { key: '$200-$500M', text: '$200-$500M', value: '$200-$500M' },
    { key: '$100-$200M', text: '$100-$200M', value: '$100-$200M' },
    { key: '$50-$100M', text: '$50-$100M', value: '$50-$100M' },
    { key: '$20-$50M', text: '$20-$50M', value: '$20-$50M' },
    { key: '$10-$20M', text: '$10-$20M', value: '$10-$20M' },
    { key: '$5-$10M', text: '$5-$10M', value: '$5-$10M' },
    { key: '<$5M', text: '<$5M', value: '<$5M' },
];

const targetDealSizeOptions = [
    { key: '<$1M', text: '<$1M', value: '<$1M' },
    { key: '$1M-$5M', text: '$1M-$5M', value: '$1M-$5M' },
    { key: '$5M-$10M', text: '$5M-$10M', value: '$5M-$10M' },
    { key: '$10M-$25M', text: '$10M-$25M', value: '$10M-$25M' },
    { key: '$25M-$50M', text: '$25M-$50M', value: '$25M-$50M' },
    { key: '$50M+', text: '$50M+', value: '$50M+' },
];

const offTakeOptions = [
    { key: 'PPA', text: 'PPA', value: 'PPA' },
    { key: 'COMMUNITY', text: 'Community', value: 'COMMUNITY' },
    { key: 'UTILITY', text: 'Utility', value: 'UTILITY' },
];

const projectStageOptions = [
    { key: 'EARLY_DEV', text: 'Early dev', value: 'EARLY_DEV' },
    { key: 'MID_DEV', text: 'Mid dev', value: 'MID_DEV' },
    { key: 'AT_OR_NEARING_NTP', text: 'At or nearing NTP', value: 'AT_OR_NEARING_NTP' },
    { key: 'MID_CONSTRUCTION', text: 'Mid-Construction', value: 'MID_CONSTRUCTION' },
    { key: 'OPERATING', text: 'Operating', value: 'OPERATING' },
];

const technologyOptions = [
    { key: 'PV', text: 'PV', value: 'PV' },
    { key: 'BESS_PV', text: 'BESS with PV', value: 'BESS_PV' },
    { key: 'BESS_STANDALONE', text: 'BESS standalone', value: 'BESS_STANDALONE' },
    { key: 'MICROGRIDS', text: 'Microgrids', value: 'MICROGRIDS' },
    { key: 'CHP', text: 'CHP', value: 'CHP' },
    { key: 'EV_CHARGING', text: 'EV Charging', value: 'EV_CHARGING' },
    { key: 'OTHER', text: 'Other', value: 'OTHER' },
];

type BuyerNetworkFiltersType = {
    maCapacity: string;
    targetDealSize: string;
    offTake: string;
    projectStage: string;
    technology: string;
    state: string;
    badgeIds: Array<string | number>;
};

const BuyerNetworkFilters: React.FC<FilterProps> = ({ onFilterChange, triggerReset }) => {
    const [filters, setFilters] = useState<BuyerNetworkFiltersType>({
        maCapacity: '',
        targetDealSize: '',
        offTake: '',
        projectStage: '',
        technology: '',
        state: '',
        badgeIds: [],
    });
    const [badges, setBadges] = useState([]);
    const [loadingBadges, setLoadingBadges] = useState(false);

    useEffect(() => {
        fetchBadges();
    }, []);

    useEffect(() => {
        if (triggerReset) {
            handleReset();
        }
    }, [triggerReset]);

    const fetchBadges = async () => {
        setLoadingBadges(true);
        try {
            const data = await getInvestorProfileBadges();
            setBadges(data);
        } catch (error) {
            console.error('Error fetching badges:', error);
        } finally {
            setLoadingBadges(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<any>, { name, value, type, checked }: any) => {
        if (name === 'badges') {
            setFilters((prev) => ({
                ...prev,
                badgeIds: value,
            }));
            return;
        }
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const applyFilters = () => {
        onFilterChange(filters);
    };

    const handleReset = () => {
        setFilters({
            maCapacity: '',
            targetDealSize: '',
            offTake: '',
            projectStage: '',
            technology: '',
            state: '',
            badgeIds: [],
        });
        onFilterChange({
            maCapacity: '',
            targetDealSize: '',
            offTake: '',
            projectStage: '',
            technology: '',
            state: '',
            badgeIds: [],
        });
    };


    return (
        <Form className="marketplace-filters">
            <Icon
                name="close"
                link
                floated="right"
                style={{ marginTop: '0.5em', marginRight: '0.5em', cursor: 'pointer', position: 'absolute', right: '0px', top: '-4px' }}
                onClick={applyFilters}
            />
            <h2>Buyer Filters</h2>
            <Form.Field>
                <label>Highlights</label>
                <Select
                    id="badge-select"
                    placeholder="Select badges..."
                    options={badges.map((badge: any) => ({
                        key: badge.id,
                        text: (
                            <div className="badge-select-option" style={{ display: 'flex', alignItems: 'center' }}>
                                <CompanyBadge key={badge.id} badge={badge} />
                                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '8px' }}>
                                    <strong>{badge.name}</strong>
                                </div>
                            </div>
                        ),
                        value: badge.id,
                    }))}
                    name="badges"
                    value={filters.badgeIds}
                    onChange={handleChange}
                    selectOnBlur={false}
                    clearable
                    multiple
                    loading={loadingBadges}
                />
            </Form.Field>
            <Form.Field>
                <label>M&A Capacity</label>
                <Select
                    placeholder="Select a value..."
                    options={maCapacityOptions}
                    name="maCapacity"
                    value={filters.maCapacity}
                    onChange={(_, data) => handleChange(_, data)}
                    selectOnBlur={false}
                    clearable
                />
            </Form.Field>
            <Form.Field>
                <label>Target deal size</label>
                <Select
                    placeholder="Select a deal size..."
                    options={targetDealSizeOptions}
                    name="targetDealSize"
                    value={filters.targetDealSize}
                    onChange={(_, data) => handleChange(_, data)}
                    selectOnBlur={false}
                    clearable
                />
            </Form.Field>
            <Form.Field>
                <label>Project stage</label>
                <Select
                    placeholder="Select a project stage..."
                    options={projectStageOptions}
                    name="projectStage"
                    value={filters.projectStage}
                    onChange={handleChange}
                    selectOnBlur={false}
                    clearable
                />
            </Form.Field>
            <Form.Field>
                <label>Offtake</label>
                <Select
                    placeholder="Select an offtake type..."
                    options={offTakeOptions}
                    name="offTake"
                    value={filters.offTake}
                    onChange={handleChange}
                    selectOnBlur={false}
                    clearable
                />
            </Form.Field>
            <Form.Field>
                <label>State</label>
                <Select
                    placeholder="Choose a state..."
                    options={STATES}
                    name="state"
                    value={filters.state}
                    onChange={handleChange}
                    selectOnBlur={false}
                    clearable
                />
            </Form.Field>
            <Form.Field>
                <label>Technology</label>
                <Select
                    placeholder="Select a technology..."
                    options={technologyOptions}
                    name="technology"
                    value={filters.technology}
                    onChange={handleChange}
                    selectOnBlur={false}
                    clearable
                />
            </Form.Field>
            <Button primary onClick={applyFilters} style={{ marginRight: '8px' }}>
                Apply Filters
            </Button>
            <Button outlined onClick={handleReset}>
                Reset Filters
            </Button>
        </Form>
    );
};

export default BuyerNetworkFilters;